import RACAccountBUForm from "@/apps/tatar/invoiceConfigApp/views/AccountingConfig/RACAccountBUForm";
import RACAccountForm from "@/apps/tatar/invoiceConfigApp/views/AccountingConfig/RACAccountForm";
import DependingCalculationField from "@/components/Form/Fields/DependingCalculationField";
import FormFieldValues from "@/components/Form/Fields/FormFieldValues";
import FormStruct from "@/components/Form/FormStruct/FormStruct";
import { FV } from "@/components/Form/Validation/FormValidators";
import ModalManager from "@/components/ModalComponent/ModalManager";
import i18n from "@/i18n";
import { AssetTypes } from "@/model/AssetTypes";
import BFCheckbox from "@/modules/abstract-ui/forms/checkbox/BFCheckbox";
import BFAssetChooser from "@/modules/abstract-ui/forms/chooser/BFAssetChooser";
import BFDatefield from "@/modules/abstract-ui/forms/datefield/BFDatefield";
import BFInput from "@/modules/abstract-ui/forms/input/BFInput";
import BFButton from "@/modules/abstract-ui/general/Button/BFButton";
import BFOverlay from "@/modules/abstract-ui/general/whisper/BFOverlay";
import BfIcon from "@/modules/abstract-ui/icon/BfIcon";
import { DefaultIcons } from "@/modules/abstract-ui/icon/DefaultIcons";
import InvoiceStruct from "@/redux/actions/struct/implemented/InvoiceStruct";
import CacheService from "@/services/CacheService";
import PermissionService from "@/services/PermissionService";
import { when } from "@/utils/Helpers";
import MQ from "@/utils/MatchQueryUtils";
import StringUtils from "@/utils/StringUtils";
import { Field } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { Loader } from "rsuite";
import Collapse from "rsuite/esm/Animation/Collapse";
import InvoiceService from "../../InvoiceService";
import {
  Invoice,
  InvoiceAccount,
  InvoiceAccountBU,
  InvoiceAccountDataCategory,
  ManualBookingData,
} from "../../RAInterfaces";
import "./AddManualPayment.scss";

interface AddManualPaymentProps {
  invoice: Invoice;
  onClose: () => void;
  onSuccess: () => void;
}

const AddManualPayment = (props: AddManualPaymentProps) => {
  return (
    <FormStruct<ManualBookingData>
      initialValues={{
        amount: props.invoice.data.opos?.openSum7Days,
        comment: "",
        withBooking: false,
      }}
      title={i18n.t(
        "ra:InvoiceDetails.Payments.AddManualPayment",
        "Manuelle Zahlung hinzufügen"
      )}
      submitText={i18n.t("Global.Buttons.add")}
      onSubmit={async (values) => {
        await InvoiceService.addManualBooking(props.invoice._id, values);
        props.onSuccess();
      }}
      onAbort={props.onClose}
      className={`add-manual-payment`}
      render={(formProps) => (
        <>
          <div className={`__field`}>
            <Field name={`amount`} validate={FV.required()}>
              {({ input, meta }) => (
                <BFInput
                  {...input}
                  {...FV.getValidation(meta)}
                  textAlign="right"
                  label={i18n.t("cb:AddManualPayment.amount", "Betrag")}
                  placeholder={i18n.t("cb:AddManualPayment.amount", "Betrag")}
                  type="priceInput"
                  suffix={StringUtils.getCurrencySymbol()}
                />
              )}
            </Field>
          </div>
          <div className={`__field`}>
            <Field name={`comment`} validate={FV.required()}>
              {({ input, meta }) => (
                <BFInput
                  type="textarea"
                  {...input}
                  {...FV.getValidation(meta)}
                  autoResize
                  label={i18n.t("cb:AddManualPayment.comment", "Kommentar")}
                  placeholder={i18n.t(
                    "cb:AddManualPayment.comment",
                    "Kommentar"
                  )}
                />
              )}
            </Field>
          </div>
          <div className={`__field`}>
            <Field name={`withBooking`}>
              {({ input, meta }) => (
                <BFCheckbox
                  checked={input.value}
                  onChange={(_, checked) => {
                    input.onChange(checked);
                  }}
                >
                  {i18n.t(
                    "cb:AddManualPayment.withBooking",
                    "Buchungssatz erstellen"
                  )}
                </BFCheckbox>
              )}
            </Field>
          </div>
          <FormFieldValues names={[`withBooking`]}>
            {([withBooking]) => {
              const disableEditAccounting =
                Boolean(
                  PermissionService.hasBusinessUnitRole(
                    "invConf_accounting",
                    false,
                    false
                  )
                ) === false;
              return (
                <Collapse in={withBooking} unmountOnExit>
                  <div>
                    <div className={`booking-form-fields`}>
                      <Field
                        name={`booking.accountNumberCode`}
                        validate={FV.compose(FV.required())}
                      >
                        {({ input, meta }) => (
                          <>
                            <BFInput
                              noBorderRadiusBottom
                              noBorderRadiusTop
                              prefix={i18n.t(
                                "ra:RABookInvoiceForm.accountNumber",
                                "Buchungskonto"
                              )}
                              removeSuffixPadding
                              suffix={
                                <BFOverlay
                                  trigger="click"
                                  enterable
                                  placement="bottomEnd"
                                  speakerFC={(close) => (
                                    <div>
                                      <BFAssetChooser
                                        height={500}
                                        // value={input.value}
                                        focusOnMount
                                        assetType={AssetTypes.Invoicing.Account}
                                        matchQuery={MQ.and(
                                          MQ.eq(
                                            "data.entity",
                                            props.invoice.data.entity
                                          )
                                        )}
                                        groups={[
                                          {
                                            matchQuery: MQ.eq(
                                              "data.category",
                                              InvoiceAccountDataCategory.GENERAL_LEDGER
                                            ),
                                            label: i18n.t(
                                              "ra:RABookInvoiceForm.generalLedger",
                                              "Sachkonto"
                                            ),
                                            value:
                                              InvoiceAccountDataCategory.GENERAL_LEDGER,
                                          },
                                          {
                                            matchQuery: MQ.eq(
                                              "data.category",
                                              InvoiceAccountDataCategory.DEBTOR
                                            ),
                                            label: i18n.t(
                                              "ra:RABookInvoiceForm.debitor",
                                              "Debitoren"
                                            ),
                                            value:
                                              InvoiceAccountDataCategory.DEBTOR,
                                          },
                                          {
                                            matchQuery: MQ.eq(
                                              "data.category",
                                              InvoiceAccountDataCategory.CREDITOR
                                            ),
                                            label: i18n.t(
                                              "ra:RABookInvoiceForm.creditor",
                                              "Kreditoren"
                                            ),
                                            value:
                                              InvoiceAccountDataCategory.CREDITOR,
                                          },
                                          {
                                            matchQuery: MQ.eq(
                                              "data.category",
                                              InvoiceAccountDataCategory.NONE
                                            ),
                                            label: i18n.t(
                                              "ra:RABookInvoiceForm.none",
                                              "Sonstiges"
                                            ),
                                            value:
                                              InvoiceAccountDataCategory.NONE,
                                          },
                                        ]}
                                        getOption={(node: InvoiceAccount) => ({
                                          label: node.data.description,
                                          value: node._id,
                                          subLabel: node.data.accountNumber,
                                        })}
                                        onChange={(
                                          value,
                                          asset: InvoiceAccount
                                        ) => {
                                          input.onChange(
                                            asset.data.accountNumber
                                          );
                                          close();
                                        }}
                                      />
                                    </div>
                                  )}
                                >
                                  <BFButton
                                    appearance="link"
                                    size="xxs"
                                    onClick={() => {}}
                                  >
                                    <BfIcon
                                      {...DefaultIcons.SEARCH}
                                      size="xxs"
                                    />
                                  </BFButton>
                                </BFOverlay>
                              }
                              {...input}
                            />
                          </>
                        )}
                      </Field>

                      <div className={`additional-info-row`}>
                        <div className={`left`} />
                        <div className={`additiona-info-value`}>
                          <DependingCalculationField
                            validate={FV.compose(FV.required())}
                            throttle={500}
                            dependingField={`booking.accountNumberCode`}
                            name={`booking.accountNumber`}
                            calulationPromise={async (value) => {
                              const invoiceAccount =
                                (await CacheService.getData({
                                  assetType: AssetTypes.Invoicing.Account,
                                  query: MQ.and(
                                    MQ.eq("data.accountNumber", value),
                                    MQ.eq(
                                      "data.entity",
                                      props.invoice.data.entity
                                    )
                                  ),
                                  forceReload: true,
                                  oType: "asset",
                                })) as InvoiceAccount;

                              if (invoiceAccount) {
                                if (invoiceAccount.data.defaultBU) {
                                  const invoiceBU = await CacheService.getData({
                                    assetType: AssetTypes.Invoicing.AccountBU,
                                    id: invoiceAccount.data.defaultBU,
                                    oType: "asset",
                                  });
                                  if (invoiceBU) {
                                    formProps.form.change(
                                      `booking.BUCode`,
                                      invoiceBU.data.accountNumber
                                    );
                                  }
                                }
                                return {
                                  value: invoiceAccount._id,
                                  data: invoiceAccount,
                                };
                              } else {
                                throw new Error(
                                  i18n.t(
                                    "ra:RABookInvoiceForm.AccountNumberError",
                                    "Buchungskonto nicht gefunden"
                                  )
                                );
                              }
                            }}
                            renderLoading={() => <Loader size="xs" />}
                            renderError={(error, value: string, reload) => (
                              <div className={`__error`}>
                                {error?.message || "error"}

                                <BFButton
                                  disabled={disableEditAccounting}
                                  tooltip={
                                    disableEditAccounting
                                      ? {
                                          tooltip: i18n.t(
                                            "ra:RABookInvoiceForm.disabledNoPermissionAccouns",
                                            "Sie haben keine Berechtigung, um Buchungskonten zu erstellen"
                                          ),
                                        }
                                      : undefined
                                  }
                                  appearance="link"
                                  noPadding
                                  size="xs"
                                  onClick={() => {
                                    ModalManager.show({
                                      noPadding: true,
                                      size: "sm",
                                      content: (state, setState, close) => (
                                        <RACAccountForm
                                          direction={
                                            props.invoice.data.direction
                                          }
                                          entityId={props.invoice.data.entity}
                                          onClose={close}
                                          onSubmit={(node: InvoiceAccount) => {
                                            formProps.form.change(
                                              `booking.accountNumberCode`,
                                              node.data.accountNumber
                                            );
                                            formProps.form.change(
                                              `booking.accountNumber`,
                                              node._id
                                            );
                                            close();
                                            reload();
                                          }}
                                          initialValues={{
                                            accountNumber: value,
                                          }}
                                        />
                                      ),
                                    });
                                  }}
                                >
                                  {i18n.t(
                                    "Global.Buttons.establish",
                                    "Anlegen"
                                  )}
                                </BFButton>
                              </div>
                            )}
                            renderValue={(
                              value: any,
                              data: InvoiceAccount,
                              reload
                            ) => (
                              <div className={`__flex full-width`}>
                                <div className={`__flex-1`}>
                                  {data.data.description}
                                </div>

                                <BFButton
                                  disabled={disableEditAccounting}
                                  tooltip={
                                    disableEditAccounting
                                      ? {
                                          tooltip: i18n.t(
                                            "ra:RABookInvoiceForm.disabledNoPermissionAccouns",
                                            "Sie haben keine Berechtigung, um Buchungskonten zu erstellen"
                                          ),
                                        }
                                      : undefined
                                  }
                                  appearance="link"
                                  noPadding
                                  className={`padding-5`}
                                  size="xs"
                                  onClick={() => {
                                    ModalManager.show({
                                      noPadding: true,
                                      size: "sm",
                                      content: (state, setState, close) => (
                                        <RACAccountForm
                                          account={data}
                                          direction={
                                            props.invoice.data.direction
                                          }
                                          entityId={props.invoice.data.entity}
                                          onClose={close}
                                          onSubmit={(node: InvoiceAccount) => {
                                            close();
                                            reload();
                                          }}
                                          initialValues={{
                                            accountNumber: value,
                                          }}
                                        />
                                      ),
                                    });
                                  }}
                                >
                                  <BfIcon {...DefaultIcons.EDIT} size={"xxs"} />
                                </BFButton>
                              </div>
                            )}
                          />
                        </div>
                      </div>

                      <Field name={`booking.BUCode`}>
                        {({ input, meta }) => (
                          <>
                            <BFInput
                              noBorderRadiusBottom
                              noBorderRadiusTop
                              prefix={i18n.t(
                                "ra:RABookInvoiceForm.BU",
                                "Buchungsschlüssel"
                              )}
                              removeSuffixPadding
                              suffix={
                                <BFOverlay
                                  trigger="click"
                                  enterable
                                  placement="bottomEnd"
                                  speakerFC={(close) => (
                                    <div>
                                      <BFAssetChooser
                                        height={500}
                                        // value={input.value}
                                        matchQuery={MQ.and(
                                          MQ.eq(
                                            "data.entity",
                                            props.invoice.data.entity
                                          )
                                        )}
                                        focusOnMount
                                        assetType={
                                          AssetTypes.Invoicing.AccountBU
                                        }
                                        getOption={(
                                          node: InvoiceAccountBU
                                        ) => ({
                                          label: node.data.description,
                                          value: node._id,
                                          subLabel: node.data.accountNumber,
                                        })}
                                        onChange={(
                                          value,
                                          asset: InvoiceAccountBU
                                        ) => {
                                          input.onChange(
                                            asset.data.accountNumber
                                          );
                                          close();
                                        }}
                                      />
                                    </div>
                                  )}
                                >
                                  <BFButton
                                    appearance="link"
                                    size="xxs"
                                    onClick={() => {}}
                                  >
                                    <BfIcon
                                      {...DefaultIcons.SEARCH}
                                      size="xxs"
                                    />
                                  </BFButton>
                                </BFOverlay>
                              }
                              {...input}
                              {...FV.getValidation(meta)}
                            />
                          </>
                        )}
                      </Field>

                      <div className={`additional-info-row`}>
                        <div className={`left`} />
                        <div className={`additiona-info-value`}>
                          <DependingCalculationField
                            throttle={500}
                            dependingField={`booking.BUCode`}
                            name={`booking.BU`}
                            calulationPromise={async (value) => {
                              const invoiceAccountBU =
                                await CacheService.getData({
                                  assetType: AssetTypes.Invoicing.AccountBU,
                                  query: MQ.and(
                                    MQ.eq("data.accountNumber", value),
                                    MQ.eq(
                                      "data.entity",
                                      props.invoice.data.entity
                                    )
                                  ),
                                  oType: "asset",
                                  forceReload: true,
                                });
                              if (invoiceAccountBU) {
                                return {
                                  value: invoiceAccountBU._id,
                                  data: invoiceAccountBU,
                                };
                              } else {
                                throw new Error(
                                  i18n.t(
                                    "ra:RABookInvoiceForm.BUError",
                                    "Buchungsschlüssel nicht gefunden"
                                  )
                                );
                              }
                            }}
                            renderLoading={() => <Loader size="xs" />}
                            renderError={(error, value: string, reload) => (
                              <div className={`__error`}>
                                {error?.message || "error"}

                                <BFButton
                                  disabled={disableEditAccounting}
                                  tooltip={
                                    disableEditAccounting
                                      ? {
                                          tooltip: i18n.t(
                                            "ra:RABookInvoiceForm.disabledNoPermissionBU",
                                            "Sie haben keine Berechtigung, um Buchungsschlüssel zu erstellen"
                                          ),
                                        }
                                      : undefined
                                  }
                                  appearance="link"
                                  noPadding
                                  size="xs"
                                  onClick={() => {
                                    ModalManager.show({
                                      noPadding: true,
                                      size: "sm",
                                      content: (state, setState, close) => (
                                        <RACAccountBUForm
                                          direction={
                                            props.invoice.data.direction
                                          }
                                          entityId={props.invoice.data.entity}
                                          onClose={close}
                                          onSubmit={(
                                            node: InvoiceAccountBU
                                          ) => {
                                            formProps.form.change(
                                              `booking.BUCode`,
                                              node.data.accountNumber
                                            );
                                            formProps.form.change(
                                              `booking.BU`,
                                              node._id
                                            );
                                            close();
                                            reload();
                                          }}
                                          initialValues={{
                                            accountNumber: value,
                                          }}
                                        />
                                      ),
                                    });
                                  }}
                                >
                                  {i18n.t(
                                    "Global.Buttons.establish",
                                    "Anlegen"
                                  )}
                                </BFButton>
                              </div>
                            )}
                            renderValue={(value, data, reload) => (
                              <div className={`__flex full-width`}>
                                <div className={`__flex-1`}>
                                  {data.data.description}
                                </div>

                                <BFButton
                                  disabled={disableEditAccounting}
                                  tooltip={
                                    disableEditAccounting
                                      ? {
                                          tooltip: i18n.t(
                                            "ra:RABookInvoiceForm.disabledNoPermissionBU",
                                            "Sie haben keine Berechtigung, um Buchungsschlüssel zu erstellen"
                                          ),
                                        }
                                      : undefined
                                  }
                                  appearance="link"
                                  noPadding
                                  className={`padding-5`}
                                  size="xs"
                                  onClick={() => {
                                    ModalManager.show({
                                      noPadding: true,
                                      size: "sm",
                                      content: (state, setState, close) => (
                                        <RACAccountBUForm
                                          accountBU={data}
                                          direction={
                                            props.invoice.data.direction
                                          }
                                          entityId={props.invoice.data.entity}
                                          onClose={close}
                                          onSubmit={(
                                            node: InvoiceAccountBU
                                          ) => {
                                            close();
                                            reload();
                                          }}
                                        />
                                      ),
                                    });
                                  }}
                                >
                                  <BfIcon {...DefaultIcons.EDIT} size={"xxs"} />
                                </BFButton>
                              </div>
                            )}
                          />
                        </div>
                      </div>

                      <Field
                        name={`booking.referenceField`}
                        validate={FV.compose(FV.required())}
                      >
                        {({ input, meta }) => (
                          <>
                            <BFInput
                              noBorderRadiusBottom
                              noBorderRadiusTop
                              prefix={i18n.t(
                                "ra:RABookInvoiceForm.referenceField",
                                "Belegfeld 1"
                              )}
                              {...input}
                              {...FV.getValidation(meta)}
                            />
                          </>
                        )}
                      </Field>
                      <Field
                        name={`booking.date`}
                        validate={FV.compose(
                          FV.required(),
                          when(
                            InvoiceStruct.getInvoiceBookingEndDate(
                              props.invoice.data.entity
                            ),
                            (date) =>
                              FV.dateAfter(
                                date,
                                i18n.t(
                                  "ra:RABookInvoiceForm.dateBookingEnd",
                                  "Der Buchungszeitraum des abgeschlossenen Geschäftsjahres ist bereits beendet. Neue Buchungen können ab dem {{dateFrom}} vorgenommen werden.",
                                  { dateFrom: StringUtils.formatDate(date) }
                                )
                              )
                          )
                        )}
                      >
                        {({ input, meta }) => (
                          <>
                            <BFDatefield
                              noBorderRadiusBottom
                              noBorderRadiusTop
                              prefix={i18n.t(
                                "ra:RABookInvoiceForm.date",
                                "Datum"
                              )}
                              {...input}
                              {...FV.getValidation(meta)}
                            />
                          </>
                        )}
                      </Field>
                      <Field name={`booking.serviceDate`}>
                        {({ input, meta }) => (
                          <>
                            <BFDatefield
                              noBorderRadiusBottom
                              noBorderRadiusTop
                              prefix={i18n.t(
                                "ra:RABookInvoiceForm.serviceDate",
                                "Leistungsdatum"
                              )}
                              {...input}
                              {...FV.getValidation(meta)}
                            />
                          </>
                        )}
                      </Field>
                      <Field name={`booking.cost1`}>
                        {({ input, meta }) => (
                          <>
                            <BFInput
                              noBorderRadiusBottom
                              noBorderRadiusTop
                              prefix={i18n.t(
                                "ra:RABookInvoiceForm.cost1",
                                "KOST 1"
                              )}
                              {...input}
                              {...FV.getValidation(meta)}
                            />
                          </>
                        )}
                      </Field>
                      <Field name={`booking.cost2`}>
                        {({ input, meta }) => (
                          <>
                            <BFInput
                              noBorderRadiusBottom
                              noBorderRadiusTop
                              prefix={i18n.t(
                                "ra:RABookInvoiceForm.cost2",
                                "KOST 2"
                              )}
                              {...input}
                              {...FV.getValidation(meta)}
                            />
                          </>
                        )}
                      </Field>

                      <FormFieldValues names={[`booking.extraInfo`]}>
                        {([extraInfo]) => (
                          <Field
                            name={`booking.usage`}
                            validate={FV.compose(FV.required())}
                          >
                            {({ input, meta }) => (
                              <>
                                <BFInput
                                  noBorderRadiusBottom={extraInfo?.length > 0}
                                  noBorderRadiusTop
                                  prefix={i18n.t(
                                    "ra:RABookInvoiceForm.usage",
                                    "Buchungstext"
                                  )}
                                  {...input}
                                  {...FV.getValidation(meta)}
                                />
                              </>
                            )}
                          </Field>
                        )}
                      </FormFieldValues>
                      <FieldArray<string> name={`booking.extraInfo`}>
                        {({ fields }) => (
                          <>
                            {fields?.map((name, index) => (
                              <Field
                                name={name}
                                key={index}
                                validate={FV.required()}
                              >
                                {({ input, meta }) => (
                                  <>
                                    <BFInput
                                      noBorderRadiusBottom={
                                        index < fields.length - 1
                                      }
                                      noBorderRadiusTop
                                      prefix={
                                        i18n.t(
                                          "ra:RABookInvoiceForm.extraInfo",
                                          "Zusatzinfo"
                                        ) +
                                        " " +
                                        (index + 1)
                                      }
                                      {...input}
                                      {...FV.getValidation(meta)}
                                      removeSuffixPadding
                                      suffix={
                                        <div>
                                          <BFButton
                                            appearance="link"
                                            size="sm"
                                            onClick={() => fields.remove(index)}
                                          >
                                            <BfIcon
                                              {...DefaultIcons.CLOSE}
                                              size="xxs"
                                            />
                                          </BFButton>
                                        </div>
                                      }
                                    />
                                  </>
                                )}
                              </Field>
                            ))}
                            <div className={`__flex __justify-center`}>
                              <BFButton
                                appearance="link"
                                size="xs"
                                onClick={() => fields.push("")}
                              >
                                {i18n.t(
                                  "ra:RABookInvoiceForm.addExtraInfo",
                                  "Zusatzinfo anlegen"
                                )}
                              </BFButton>
                            </div>
                          </>
                        )}
                      </FieldArray>
                    </div>
                  </div>
                </Collapse>
              );
            }}
          </FormFieldValues>
        </>
      )}
    />
  );
};

export default AddManualPayment;
